<template>
  <div class="config-container">
    <div class="header">
      <h2>Game Configuration</h2>
      <button class="back-btn" @click="router.back()">Back</button>
    </div>

    <div class="games-list">
      <div v-for="game in games" :key="game.id" class="game-item">
        <div class="game-header">
          <h3>{{ game.title }}</h3>
          <div class="actions">
            <button @click="editGame(game)" class="edit-btn">Edit</button>
            <button @click="deleteGame(game.id)" class="delete-btn">Delete</button>
          </div>
        </div>
        
        <div class="game-content">
          <div class="preview">
            <img :src="game.thumbnail" :alt="game.title">
          </div>
          <div class="info">
            <p><strong>Description:</strong> {{ game.description }}</p>
            <p><strong>Category:</strong> {{ game.category }}</p>
            <p><strong>URL:</strong> {{ game.url }}</p>
            <div class="instructions">
              <strong>Instructions:</strong>
              <div v-html="game.instructions"></div>
            </div>
            <div class="screenshots">
              <strong>Screenshots:</strong>
              <div class="screenshot-list">
                <img v-for="(screenshot, index) in game.screenshots" 
                     :key="index" 
                     :src="screenshot" 
                     :alt="game.title + ' screenshot ' + (index + 1)">
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <button class="add-btn" @click="showAddModal = true">Add New Game</button>

    <!-- Edit/Add game modal -->
    <div v-if="showAddModal" class="modal">
      <div class="modal-content">
        <h3>{{ editingGame ? 'Edit Game' : 'Add New Game' }}</h3>
        <form @submit.prevent="handleSubmit">
          <div class="form-group">
            <label>Title</label>
            <input v-model="formData.title" required>
          </div>
          <div class="form-group">
            <label>Category</label>
            <input v-model="formData.category" required>
          </div>
          <div class="form-group">
            <label>Description</label>
            <textarea v-model="formData.description" required></textarea>
          </div>
          <div class="form-group">
            <label>Thumbnail URL</label>
            <input v-model="formData.thumbnail" required>
          </div>
          <div class="form-group">
            <label>Game URL</label>
            <input v-model="formData.url" required>
          </div>
          <div class="form-group">
            <label>Instructions (HTML supported)</label>
            <textarea v-model="formData.instructions" required></textarea>
          </div>
          <div class="form-group">
            <label>Screenshots</label>
            <div class="screenshot-inputs">
              <div v-for="(url, index) in formData.screenshots" :key="index" class="screenshot-input">
                <input v-model="formData.screenshots[index]" placeholder="Enter image URL">
                <button type="button" @click="removeScreenshot(index)" class="remove-btn">Remove</button>
              </div>
              <button type="button" @click="addScreenshot" class="add-screenshot-btn">Add Screenshot</button>
            </div>
          </div>
          <div class="form-actions">
            <button type="button" @click="showAddModal = false">Cancel</button>
            <button type="submit">Save</button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useGameStore } from '../stores/gameStore'
import { useUserStore } from '../stores/userStore'

const router = useRouter()
const gameStore = useGameStore()
const userStore = useUserStore()

const games = ref([])
const showAddModal = ref(false)
const editingGame = ref(null)

const formData = ref({
  title: '',
  category: '',
  description: '',
  thumbnail: '',
  url: '',
  instructions: '',
  screenshots: []
})

onMounted(async () => {
  if (!userStore.isLoggedIn) {
    router.push('/login')
    return
  }
  await loadGames()
})

const loadGames = async () => {
  try {
    const response = await fetch('/api/games')
    if (!response.ok) {
      throw new Error('Loading failed')
    }
    const data = await response.json()
    games.value = data.games
  } catch (error) {
    console.error('Failed to load game data:', error)
    alert('Loading failed. Please try again.')
  }
}

const editGame = (game) => {
  editingGame.value = game
  formData.value = { 
    ...game,
    screenshots: [...(game.screenshots || [])]
  }
  showAddModal.value = true
}

const deleteGame = async (id) => {
  if (!confirm('Are you sure you want to delete this game?')) return
  
  games.value = games.value.filter(g => g.id !== id)
  await saveGames()
}

const addScreenshot = () => {
  formData.value.screenshots.push('')
}

const removeScreenshot = (index) => {
  formData.value.screenshots.splice(index, 1)
}

const handleSubmit = async () => {
  // Filter out empty screenshot URLs
  formData.value.screenshots = formData.value.screenshots.filter(url => url.trim() !== '')
  
  if (editingGame.value) {
    const index = games.value.findIndex(g => g.id === editingGame.value.id)
    games.value[index] = { ...editingGame.value, ...formData.value }
  } else {
    const newGame = {
      id: games.value.length + 1,
      ...formData.value,
      plays: 0,
      likes: 0,
      rating: 0,
      comments: []
    }
    games.value.push(newGame)
  }
  
  await saveGames()
  showAddModal.value = false
  editingGame.value = null
  formData.value = {
    title: '',
    category: '',
    description: '',
    thumbnail: '',
    url: '',
    instructions: '',
    screenshots: []
  }
}

const saveGames = async () => {
  const data = {
    last_id: Math.max(...games.value.map(g => g.id)),
    games: games.value
  }
  
  try {
    const response = await fetch('/api/games', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    })
    
    if (!response.ok) {
      throw new Error('Saving failed')
    }

    const result = await response.json()
    console.log(result.message)
  } catch (error) {
    console.error('Failed to save game data:', error)
    alert('Saving failed. Please try again.')
  }
}
</script>

<style scoped>
.config-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
}

.games-list {
  display: grid;
  gap: 20px;
}

.game-item {
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.game-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.game-content {
  display: grid;
  grid-template-columns: 200px 1fr;
  gap: 20px;
}

.preview {
  border: 1px solid #ddd;
  border-radius: 8px;
  overflow: hidden;
  aspect-ratio: 4/3;
}

.preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.info {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.instructions {
  margin-top: 10px;
}

.screenshot-list {
  display: flex;
  gap: 10px;
  overflow-x: auto;
  padding: 10px 0;
}

.screenshot-list img {
  width: 150px;
  height: 100px;
  object-fit: cover;
  border-radius: 4px;
}

.actions {
  display: flex;
  gap: 10px;
}

.edit-btn, .delete-btn {
  padding: 6px 12px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.edit-btn {
  background: #1E90FF;
  color: white;
}

.delete-btn {
  background: #ff4444;
  color: white;
}

.add-btn {
  margin-top: 20px;
  padding: 12px 24px;
  background: #9370DB;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  overflow-y: auto;
  padding: 20px;
}

.modal-content {
  background: white;
  padding: 30px;
  border-radius: 12px;
  width: 100%;
  max-width: 800px;
  margin: auto;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: bold;
}

.form-group input,
.form-group textarea {
  width: 100%;
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
}

.form-group textarea {
  min-height: 100px;
  resize: vertical;
}

.screenshot-inputs {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.screenshot-input {
  display: flex;
  gap: 10px;
}

.screenshot-input input {
  flex: 1;
}

.remove-btn {
  padding: 4px 8px;
  background: #ff4444;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.add-screenshot-btn {
  padding: 8px;
  background: #1E90FF;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  margin-top: 10px;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
}

.form-actions button {
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.form-actions button[type="submit"] {
  background: #1E90FF;
  color: white;
}

.form-actions button[type="button"] {
  background: #ddd;
}
</style> 